/**
 * Field Mapping Registry
 *
 * Two registries:
 *   1. CONNECTOR_FIELDS  — what each external connector exposes as mappable fields
 *   2. INTERNAL_FIELDS   — every Priorities.ai field type a connector can map to/from
 *
 * Field types:
 *   text       — free text, maps directly
 *   number     — numeric, maps directly or via scale
 *   enum       — fixed set of values; requires a value-level sub-mapping
 *   multi_enum — multiple values; each value can be mapped
 *   date       — date/time, maps directly
 *   boolean    — true/false
 */

// ── Shared types ──────────────────────────────────────────────────────────────

export type FieldType = 'text' | 'number' | 'enum' | 'multi_enum' | 'date' | 'boolean';
export type MappingDirection = 'import' | 'export' | 'both';

export interface FieldEnumValue {
  value: string;
  label: string;
  color?: string; // hex, for color swatches
}

export interface ExternalFieldDef {
  key: string;
  label: string;
  type: FieldType;
  description?: string;
  direction: MappingDirection;
  enumValues?: FieldEnumValue[];
}

export interface InternalFieldDef {
  key: string;
  label: string;
  type: FieldType;
  category: InternalFieldCategory;
  description?: string;
  enumValues?: FieldEnumValue[]; // static enum values where known
}

export type InternalFieldCategory =
  | 'standard'        // title, description, status, due_date, external_key, parent_item, target_release
  | 'lifecycle'       // planning_maturity, resolution (why closed)
  | 'category'        // item category / language type
  | 'ownership'       // stewardship bucket, strategic bucket
  | 'altitude'        // altitude level
  | 'attribute'       // scoring attribute (importance, effort, confidence…)
  | 'value_scale'     // value scale entry
  | 'source'          // idea source
  | 'cue'             // cue / contextual tag
  | 'custom_field'    // custom field
  | 'relationship_type'; // retained for stored-config compat; the field entry is retired (v3 relationshipMap replaces it)

// ── Priorities.ai internal field registry ────────────────────────────────────

export const INTERNAL_FIELDS: InternalFieldDef[] = [
  // Standard item fields
  { key: 'title',       label: 'Title',        type: 'text',    category: 'standard',   description: 'The primary name / headline of the item' },
  { key: 'description', label: 'Description',  type: 'text',    category: 'standard',   description: 'Long-form context or problem statement' },
  { key: 'status',      label: 'Status',       type: 'enum',    category: 'standard',   description: 'Active, Archived, etc.',
    enumValues: [
      { value: 'active',    label: 'Active' },
      { value: 'archived',  label: 'Archived' },
      { value: 'draft',     label: 'Draft' },
    ],
  },

  // Item category
  {
    key: 'category', label: 'Item Category', type: 'enum', category: 'category',
    description: 'The type / classification of the item',
    enumValues: [
      { value: 'idea',           label: 'Idea' },
      { value: 'feature',        label: 'Feature' },
      { value: 'improvement',    label: 'Improvement' },
      { value: 'bug',            label: 'Bug' },
      { value: 'initiative',     label: 'Initiative' },
      { value: 'research',       label: 'Research' },
      { value: 'infrastructure', label: 'Infrastructure' },
    ],
  },

  // Altitude
  {
    key: 'altitude', label: 'Altitude', type: 'enum', category: 'altitude',
    description: 'Strategic level: how high in the hierarchy does this sit?',
    // Canonical 1–5 scale (itemLifecycle.ts AltitudeLevel). Was 0–4 here, which
    // made UI-built value maps ship an off-by-one (audit defect #8).
    enumValues: [
      { value: '5', label: 'Horizon (Strategic)' },
      { value: '4', label: 'Epic (Initiative)' },
      { value: '3', label: 'Feature (Tactical)' },
      { value: '2', label: 'Task (Operational)' },
      { value: '1', label: 'Sub-task (Ground)' },
    ],
  },

  // Scoring attributes (commonly used — workspace can add more via Attributes settings)
  { key: 'attribute:importance',  label: 'Attribute: Importance',   type: 'number', category: 'attribute', description: '1–10 importance score' },
  { key: 'attribute:effort',      label: 'Attribute: Effort',       type: 'number', category: 'attribute', description: '1–10 effort estimate' },
  { key: 'attribute:confidence',  label: 'Attribute: Confidence',   type: 'number', category: 'attribute', description: '1–10 confidence in estimates' },
  { key: 'attribute:reach',       label: 'Attribute: Reach',        type: 'number', category: 'attribute', description: 'Estimated users or audience size' },
  { key: 'attribute:risk',        label: 'Attribute: Risk',         type: 'number', category: 'attribute', description: '1–10 risk level' },
  { key: 'attribute:value',       label: 'Attribute: Value',        type: 'number', category: 'attribute', description: 'Business or customer value score' },

  // Idea source
  {
    key: 'source', label: 'Idea Source', type: 'enum', category: 'source',
    description: 'Where did this idea originate?',
    enumValues: [
      { value: 'customer',    label: 'Customer' },
      { value: 'internal',    label: 'Internal' },
      { value: 'partner',     label: 'Partner' },
      { value: 'research',    label: 'Research' },
      { value: 'support',     label: 'Support' },
      { value: 'sales',       label: 'Sales' },
      { value: 'engineering', label: 'Engineering' },
      { value: 'leadership',  label: 'Leadership' },
    ],
  },

  // Cue / contextual tag
  { key: 'cue', label: 'Cue / Tag', type: 'multi_enum', category: 'cue', description: 'Contextual signals attached to the item' },

  // Standard scheduling / reference fields
  { key: 'due_date',        label: 'Due Date',         type: 'date',   category: 'standard',   description: 'Target completion date for the item' },
  { key: 'target_release',  label: 'Target Release',   type: 'multi_enum', category: 'standard', description: 'Target product version(s) or release this item is scoped to' },
  { key: 'external_key',    label: 'External Key',     type: 'text',   category: 'standard',   description: 'External system issue key (e.g. PROJ-123) — used for bidirectional sync identification' },
  { key: 'external_id',     label: 'External ID',      type: 'text',   category: 'standard',   description: 'Stable business key on the item (items.external_id) — CSV/API upsert match key' },
  { key: 'launch_type',     label: 'Launch Type',      type: 'text',   category: 'standard',   description: 'Marketing launch classification slug' },
  { key: 'investment_type', label: 'Investment Type',  type: 'text',   category: 'standard',   description: 'Financial investment classification slug' },
  { key: 'parent_item',     label: 'Parent Item',      type: 'text',   category: 'standard',   description: 'Parent item ID or external key — establishes the hierarchical relationship' },

  // Item language type (problem / opportunity / risk / …)
  {
    key: 'item_language_type', label: 'Item Language Type', type: 'enum', category: 'category',
    description: 'How the item is framed — determines how it should be compared against other items',
    enumValues: [
      { value: 'problem',     label: 'Problem' },
      { value: 'opportunity', label: 'Opportunity' },
      { value: 'risk',        label: 'Risk' },
      { value: 'solution',    label: 'Solution' },
      { value: 'commitment',  label: 'Commitment' },
      { value: 'investment',  label: 'Investment' },
    ],
  },

  // Ownership / routing
  {
    key: 'strategic_bucket', label: 'Strategic Bucket', type: 'enum', category: 'ownership',
    description: 'Portfolio-level grouping by strategic intent',
    enumValues: [
      { value: 'growth',      label: 'Growth' },
      { value: 'commitments', label: 'Commitments' },
      { value: 'reliability', label: 'Reliability' },
      { value: 'platform',    label: 'Platform' },
      { value: 'compliance',  label: 'Compliance' },
    ],
  },
  {
    key: 'stewardship_bucket', label: 'Team / Ownership Bucket', type: 'enum', category: 'ownership',
    description: 'Functional area this item is routed to',
    enumValues: [
      { value: 'product',     label: 'Product' },
      { value: 'engineering', label: 'Engineering' },
      { value: 'operations',  label: 'Operations' },
      { value: 'marketing',   label: 'Marketing' },
      { value: 'sales',       label: 'Sales' },
      { value: 'support',     label: 'Support' },
      { value: 'hr',          label: 'HR' },
      { value: 'finance',     label: 'Finance' },
      { value: 'legal',       label: 'Legal' },
    ],
  },

  // Lifecycle state fields
  {
    key: 'planning_maturity', label: 'Planning Maturity', type: 'enum', category: 'lifecycle',
    description: 'How far along the Directional → Detailed planning continuum this item sits',
    enumValues: [
      { value: 'directional',            label: 'Directional' },
      { value: 'partially_analyzed',     label: 'Partially Analyzed' },
      { value: 'ready_for_detailed',     label: 'Ready for Detailed Planning' },
      { value: 'detailed_plan_exists',   label: 'Detailed Plan Exists' },
    ],
  },
  {
    key: 'resolution', label: 'Resolution', type: 'enum', category: 'lifecycle',
    description: 'Why this item was closed, archived, or resolved',
    enumValues: [
      { value: 'completed',    label: 'Completed' },
      { value: 'wont_do',      label: "Won't Do" },
      { value: 'duplicate',    label: 'Duplicate' },
      { value: 'deferred',     label: 'Deferred' },
      { value: 'superseded',   label: 'Superseded' },
      { value: 'out_of_scope', label: 'Out of Scope' },
    ],
  },

  // Custom fields are targets too — `custom_field:<key>` for each of the
  // workspace's own fields (Settings → Custom Fields), listed at runtime by
  // the Field Mappings page, never as fixed placeholders here.
];

export const INTERNAL_FIELD_CATEGORIES: Record<InternalFieldCategory, string> = {
  standard:          'Standard Fields',
  lifecycle:         'Lifecycle & Resolution',
  category:          'Item Category',
  ownership:         'Ownership & Routing',
  altitude:          'Altitude',
  attribute:         'Scoring Attributes',
  value_scale:       'Value Scales',
  source:            'Idea Sources',
  cue:               'Cues & Tags',
  custom_field:      'Custom Fields',
};

// ── Connector field registries ────────────────────────────────────────────────

/** Shared color vocabulary for whiteboard / Miro visual channels. */
const VISUAL_COLOR_ENUM: Array<{ value: string; label: string; color: string }> = [
  { value: 'yellow',       label: 'Yellow',       color: '#FFF000' },
  { value: 'light_yellow', label: 'Light Yellow', color: '#F5D128' },
  { value: 'orange',       label: 'Orange',       color: '#FF9900' },
  { value: 'pink',         label: 'Pink',         color: '#FF6699' },
  { value: 'red',          label: 'Red',          color: '#FF6644' },
  { value: 'purple',       label: 'Purple',       color: '#9A00FF' },
  { value: 'violet',       label: 'Violet',       color: '#9C27B0' },
  { value: 'blue',         label: 'Blue',         color: '#00AAFF' },
  { value: 'light_blue',   label: 'Light Blue',   color: '#87CEEB' },
  { value: 'cyan',         label: 'Cyan',         color: '#00E5FF' },
  { value: 'green',        label: 'Green',        color: '#00CC60' },
  { value: 'light_green',  label: 'Light Green',  color: '#ADFF2F' },
  { value: 'gray',         label: 'Gray',         color: '#9E9E9E' },
  { value: 'white',        label: 'White',        color: '#FFFFFF' },
  { value: 'black',        label: 'Black',        color: '#444444' },
];

/**
 * Miro's own sticky-note palette — the names its API reads and writes in
 * `style.fillColor`. Not the same list as the Miro `sticky_color` values below:
 * Miro calls purple "violet" and has no white.
 */
export const MIRO_STICKY_PALETTE: Array<{ name: string; hex: string; label: string }> = [
  { name: 'yellow',       hex: '#FFD02F', label: 'Yellow' },
  { name: 'light_yellow', hex: '#F5F5DC', label: 'Light Yellow' },
  { name: 'orange',       hex: '#FF9800', label: 'Orange' },
  { name: 'light_green',  hex: '#90EE90', label: 'Light Green' },
  { name: 'green',        hex: '#4CAF50', label: 'Green' },
  { name: 'dark_green',   hex: '#2E7D32', label: 'Dark Green' },
  { name: 'cyan',         hex: '#00BCD4', label: 'Cyan' },
  { name: 'light_blue',   hex: '#87CEEB', label: 'Light Blue' },
  { name: 'blue',         hex: '#2196F3', label: 'Blue' },
  { name: 'dark_blue',    hex: '#3F51B5', label: 'Dark Blue' },
  { name: 'light_pink',   hex: '#FFCDD2', label: 'Light Pink' },
  { name: 'pink',         hex: '#E91E63', label: 'Pink' },
  { name: 'violet',       hex: '#9C27B0', label: 'Violet' },
  { name: 'red',          hex: '#F44336', label: 'Red' },
  { name: 'gray',         hex: '#9E9E9E', label: 'Gray' },
  { name: 'black',        hex: '#212121', label: 'Black' },
];

export const CONNECTOR_FIELDS: Record<string, ExternalFieldDef[]> = {

  /**
   * Physical whiteboard photo import — visual channels the vision model reads
   * off the board. Flat mapping-set entries work for value maps; richer
   * multi-channel rules live in miroColorMaps (shared with Miro) via the
   * Visual Language section on Settings → Field Mappings.
   */
  whiteboard: [
    {
      key: 'shape_color', label: 'Shape Color', type: 'enum', direction: 'import',
      description: 'Fill / sticky color of a drawn shape or sticky note',
      enumValues: VISUAL_COLOR_ENUM,
    },
    {
      key: 'shape', label: 'Shape', type: 'enum', direction: 'import',
      description: 'Geometric form of the idea carrier',
      enumValues: [
        { value: 'sticky', label: 'Sticky' },
        { value: 'box', label: 'Box' },
        { value: 'rounded_box', label: 'Rounded box' },
        { value: 'circle', label: 'Circle / oval' },
        { value: 'diamond', label: 'Diamond' },
        { value: 'cloud', label: 'Cloud' },
        { value: 'text', label: 'Plain text' },
      ],
    },
    {
      key: 'line_color', label: 'Line Color', type: 'enum', direction: 'import',
      description: 'Stroke color of a plain (non-arrow) connector',
      enumValues: VISUAL_COLOR_ENUM,
    },
    {
      key: 'line_type', label: 'Line Type', type: 'enum', direction: 'import',
      description: 'Solid vs dashed stroke on a connector',
      enumValues: [
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
      ],
    },
    {
      key: 'arrow_color', label: 'Arrow Color', type: 'enum', direction: 'import',
      description: 'Stroke color of a directed arrow connector',
      enumValues: VISUAL_COLOR_ENUM,
    },
    {
      key: 'arrow_shape', label: 'Arrow Shape', type: 'enum', direction: 'import',
      description: 'Arrow head style — none (plain line), single, or double',
      enumValues: [
        { value: 'none', label: 'None (plain line)' },
        { value: 'single', label: 'Single head →' },
        { value: 'double', label: 'Double head ↔' },
      ],
    },
  ],

  miro: [
    {
      key: 'sticky_color', label: 'Sticky Note Color', type: 'enum', direction: 'import',
      description: 'Background color of the sticky note',
      enumValues: [
        { value: 'yellow',       label: 'Yellow',       color: '#FFF000' },
        { value: 'light_yellow', label: 'Light Yellow', color: '#F5D128' },
        { value: 'orange',       label: 'Orange',       color: '#FF9900' },
        { value: 'pink',         label: 'Pink',         color: '#FF6699' },
        { value: 'red',          label: 'Red',          color: '#FF6644' },
        { value: 'purple',       label: 'Purple',       color: '#9A00FF' },
        { value: 'blue',         label: 'Blue',         color: '#00AAFF' },
        { value: 'cyan',         label: 'Cyan',         color: '#00E5FF' },
        { value: 'green',        label: 'Green',        color: '#00CC60' },
        { value: 'light_green',  label: 'Light Green',  color: '#ADFF2F' },
        { value: 'white',        label: 'White',        color: '#FFFFFF' },
        { value: 'black',        label: 'Black',        color: '#444444' },
      ],
    },
    { key: 'sticky_text',  label: 'Sticky Note Text',  type: 'text',       direction: 'import',  description: 'Text content of the sticky note → maps to item title or description' },
    { key: 'frame_name',   label: 'Frame / Container', type: 'text',       direction: 'import',  description: 'Name of the Miro frame or container the note lives in' },
    { key: 'tag',          label: 'Tag',               type: 'multi_enum', direction: 'import',  description: 'Tags applied to the sticky note in Miro' },
    { key: 'board_name',   label: 'Board Name',        type: 'text',       direction: 'import',  description: 'Name of the Miro board being imported from' },
    { key: 'created_by',   label: 'Created By',        type: 'text',       direction: 'import',  description: 'Miro user who created the sticky' },
  ],

  jira: [
    { key: 'summary',      label: 'Summary (Title)',  type: 'text',       direction: 'both',    description: 'Issue title / headline' },
    { key: 'description',  label: 'Description',      type: 'text',       direction: 'both',    description: 'Issue body / acceptance criteria' },
    {
      key: 'issue_type', label: 'Issue Type', type: 'enum', direction: 'both',
      description: 'Jira issue type (Bug, Story, Epic, Task, etc.)',
      enumValues: [
        { value: 'Bug',          label: 'Bug' },
        { value: 'Story',        label: 'Story' },
        { value: 'Epic',         label: 'Epic' },
        { value: 'Task',         label: 'Task' },
        { value: 'Sub-task',     label: 'Sub-task' },
        { value: 'Improvement',  label: 'Improvement' },
        { value: 'New Feature',  label: 'New Feature' },
        { value: 'Technical',    label: 'Technical' },
      ],
    },
    {
      key: 'priority', label: 'Priority', type: 'enum', direction: 'both',
      description: 'Jira priority level',
      enumValues: [
        { value: 'Highest', label: 'Highest' },
        { value: 'High',    label: 'High' },
        { value: 'Medium',  label: 'Medium' },
        { value: 'Low',     label: 'Low' },
        { value: 'Lowest',  label: 'Lowest' },
      ],
    },
    {
      key: 'status', label: 'Status', type: 'enum', direction: 'import',
      description: 'Current workflow status',
      enumValues: [
        { value: 'Open',        label: 'Open' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'In Review',   label: 'In Review' },
        { value: 'Done',        label: 'Done' },
        { value: 'Closed',      label: 'Closed' },
        { value: 'Blocked',     label: 'Blocked' },
        { value: 'Backlog',     label: 'Backlog' },
      ],
    },
    { key: 'labels',       label: 'Labels',           type: 'multi_enum', direction: 'both',    description: 'Jira labels (free-text tags)' },
    { key: 'components',   label: 'Components',       type: 'multi_enum', direction: 'both',    description: 'Jira component names' },
    { key: 'story_points', label: 'Story Points',     type: 'number',     direction: 'both',    description: 'Effort estimate in story points' },
    { key: 'sprint',       label: 'Sprint',           type: 'text',       direction: 'import',  description: 'Sprint name the issue belongs to' },
    { key: 'project_key',  label: 'Project Key',      type: 'text',       direction: 'both',    description: 'Jira project key (e.g. PROJ)' },
    { key: 'assignee',               label: 'Assignee',               type: 'text',       direction: 'both',   description: 'Assigned user email or display name' },
    { key: 'reporter',               label: 'Reporter',               type: 'text',       direction: 'import', description: 'User who filed the issue' },
    { key: 'due_date',               label: 'Due Date',               type: 'date',       direction: 'both',   description: 'Issue due date' },
    { key: 'fix_version',            label: 'Fix Version/s',          type: 'multi_enum', direction: 'both',   description: 'Target release version(s) this issue will be resolved in' },
    { key: 'epic_link',              label: 'Epic Link',              type: 'text',       direction: 'both',   description: 'Key of the Epic this issue belongs to (e.g. PROJ-42)' },
    { key: 'epic_name',              label: 'Epic Name',              type: 'text',       direction: 'both',   description: 'Name of the Epic — set only when issue_type = Epic' },
    { key: 'parent',                 label: 'Parent Issue',           type: 'text',       direction: 'both',   description: 'Parent issue key for sub-tasks and next-gen hierarchy' },
    { key: 'issue_key',              label: 'Issue Key',              type: 'text',       direction: 'import', description: 'Jira issue key (e.g. PROJ-123) — used for sync identification; never overwrite' },
    {
      key: 'resolution', label: 'Resolution', type: 'enum', direction: 'import',
      description: 'Resolution reason when the issue is closed',
      enumValues: [
        { value: 'Fixed',             label: 'Fixed' },
        { value: "Won't Fix",         label: "Won't Fix" },
        { value: 'Duplicate',         label: 'Duplicate' },
        { value: 'Incomplete',        label: 'Incomplete' },
        { value: 'Cannot Reproduce',  label: 'Cannot Reproduce' },
        { value: 'Done',              label: 'Done' },
      ],
    },
    { key: 'environment',            label: 'Environment',            type: 'text',       direction: 'both',   description: 'Environment where the issue was observed (e.g. Production, Staging)' },
    { key: 'time_original_estimate', label: 'Original Estimate',      type: 'number',     direction: 'both',   description: 'Original time estimate in seconds (Jira time-tracking)' },
    { key: 'votes',                  label: 'Votes',                  type: 'number',     direction: 'import', description: 'Number of votes on the issue — useful as a demand-strength signal' },
    { key: 'created',                label: 'Created Date',           type: 'date',       direction: 'import', description: 'When the issue was created' },
    { key: 'updated',                label: 'Updated Date',           type: 'date',       direction: 'import', description: 'When the issue was last updated' },
    { key: 'custom_field',           label: 'Custom Field',           type: 'text',       direction: 'both',   description: 'Any Jira custom field (enter field key manually)' },
  ],

  slack: [
    { key: 'message_text',    label: 'Message Text',    type: 'text',       direction: 'import',  description: 'The full text of the Slack message' },
    { key: 'channel_name',    label: 'Channel Name',    type: 'text',       direction: 'import',  description: 'The Slack channel the message came from' },
    { key: 'reaction_emoji',  label: 'Reaction Emoji',  type: 'enum',       direction: 'import',  description: 'Emoji reaction that triggers capture' },
    { key: 'author_name',     label: 'Author Name',     type: 'text',       direction: 'import',  description: 'Display name of the message author' },
    { key: 'author_email',    label: 'Author Email',    type: 'text',       direction: 'import',  description: 'Email of the message author (if available)' },
    { key: 'thread_ts',       label: 'Thread',          type: 'text',       direction: 'import',  description: 'Thread timestamp — use to group related messages' },
    { key: 'message_ts',      label: 'Message Date',    type: 'date',       direction: 'import',  description: 'Timestamp of the original message' },
  ],

  asana: [
    { key: 'name',         label: 'Task Name',        type: 'text',       direction: 'both',    description: 'Asana task title' },
    { key: 'notes',        label: 'Notes',            type: 'text',       direction: 'both',    description: 'Task description / notes' },
    {
      key: 'priority', label: 'Priority', type: 'enum', direction: 'both',
      enumValues: [
        { value: 'high',   label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low',    label: 'Low' },
      ],
    },
    { key: 'tags',         label: 'Tags',             type: 'multi_enum', direction: 'both',    description: 'Asana tags on the task' },
    { key: 'project',      label: 'Project',          type: 'text',       direction: 'both',    description: 'Asana project name or GID' },
    { key: 'section',      label: 'Section',          type: 'text',       direction: 'both',    description: 'Section within the project' },
    { key: 'assignee',     label: 'Assignee',         type: 'text',       direction: 'both',    description: 'Assigned user email' },
    { key: 'due_date',     label: 'Due Date',         type: 'date',       direction: 'both',    description: 'Task due date' },
    { key: 'custom_field', label: 'Custom Field',     type: 'text',       direction: 'both',    description: 'Asana custom field (enter field name)' },
  ],

  linear: [
    { key: 'title',        label: 'Title',            type: 'text',       direction: 'both',    description: 'Issue title' },
    { key: 'description',  label: 'Description',      type: 'text',       direction: 'both',    description: 'Issue description (markdown)' },
    { key: 'identifier',   label: 'Issue key',        type: 'text',       direction: 'import',  description: 'The issue identifier, e.g. PRI-12' },
    {
      key: 'priority', label: 'Priority', type: 'enum', direction: 'both',
      enumValues: [
        { value: '0', label: 'No Priority' },
        { value: '1', label: 'Urgent' },
        { value: '2', label: 'High' },
        { value: '3', label: 'Medium' },
        { value: '4', label: 'Low' },
      ],
    },
    {
      key: 'state', label: 'State', type: 'enum', direction: 'both',
      enumValues: [
        { value: 'backlog',     label: 'Backlog' },
        { value: 'todo',        label: 'Todo' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'in_review',   label: 'In Review' },
        { value: 'done',        label: 'Done' },
        { value: 'cancelled',   label: 'Cancelled' },
      ],
    },
    { key: 'labels',       label: 'Labels',           type: 'multi_enum', direction: 'both',    description: 'Linear labels' },
    { key: 'team',         label: 'Team',             type: 'text',       direction: 'both',    description: 'Linear team name or key' },
    { key: 'project',      label: 'Project',          type: 'text',       direction: 'both',    description: 'Linear project name' },
    { key: 'cycle',        label: 'Cycle',            type: 'text',       direction: 'both',    description: 'Linear cycle (sprint)' },
    { key: 'estimate',     label: 'Estimate',         type: 'number',     direction: 'both',    description: 'Story points / complexity estimate' },
    { key: 'assignee',     label: 'Assignee',         type: 'text',       direction: 'both',    description: 'Assigned user email' },
  ],

  github: [
    { key: 'title',        label: 'Issue Title',      type: 'text',       direction: 'both',    description: 'GitHub issue title' },
    { key: 'body',         label: 'Body',             type: 'text',       direction: 'both',    description: 'Issue body / description' },
    { key: 'labels',       label: 'Labels',           type: 'multi_enum', direction: 'both',    description: 'GitHub issue labels' },
    {
      key: 'state', label: 'State', type: 'enum', direction: 'both',
      enumValues: [
        { value: 'open',   label: 'Open' },
        { value: 'closed', label: 'Closed' },
      ],
    },
    { key: 'milestone',    label: 'Milestone',        type: 'text',       direction: 'both',    description: 'Milestone title' },
    { key: 'assignees',    label: 'Assignees',        type: 'multi_enum', direction: 'both',    description: 'Assigned GitHub usernames' },
    { key: 'repository',   label: 'Repository',       type: 'text',       direction: 'both',    description: 'Repository name (owner/repo)' },
    { key: 'created_at',   label: 'Created Date',     type: 'date',       direction: 'import' },
    { key: 'updated_at',   label: 'Updated Date',     type: 'date',       direction: 'import' },
  ],

  clickup: [
    { key: 'name',         label: 'Task Name',        type: 'text',       direction: 'both',    description: 'ClickUp task name' },
    { key: 'description',  label: 'Description',      type: 'text',       direction: 'both',    description: 'Task description' },
    {
      key: 'priority', label: 'Priority', type: 'enum', direction: 'both',
      enumValues: [
        { value: '1', label: 'Urgent' },
        { value: '2', label: 'High' },
        { value: '3', label: 'Normal' },
        { value: '4', label: 'Low' },
      ],
    },
    {
      key: 'status', label: 'Status', type: 'enum', direction: 'both',
      enumValues: [
        { value: 'open',        label: 'Open' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'review',      label: 'Review' },
        { value: 'closed',      label: 'Closed' },
      ],
    },
    { key: 'tags',         label: 'Tags',             type: 'multi_enum', direction: 'both',    description: 'ClickUp tags' },
    { key: 'list',         label: 'List',             type: 'text',       direction: 'both',    description: 'ClickUp list name' },
    { key: 'folder',       label: 'Folder',           type: 'text',       direction: 'both',    description: 'ClickUp folder name' },
    { key: 'space',        label: 'Space',            type: 'text',       direction: 'both',    description: 'ClickUp space name' },
    { key: 'assignees',    label: 'Assignees',        type: 'multi_enum', direction: 'both' },
    { key: 'due_date',     label: 'Due Date',         type: 'date',       direction: 'both' },
    { key: 'custom_field', label: 'Custom Field',     type: 'text',       direction: 'both',    description: 'ClickUp custom field (enter field name)' },
  ],

  notion: [
    { key: 'title',        label: 'Title',            type: 'text',       direction: 'both',    description: 'Page or database entry title' },
    { key: 'content',      label: 'Page Content',     type: 'text',       direction: 'both',    description: 'Body content of the Notion page' },
    {
      key: 'status', label: 'Status', type: 'enum', direction: 'both',
      enumValues: [
        { value: 'Not started', label: 'Not started' },
        { value: 'In progress', label: 'In progress' },
        { value: 'Done',        label: 'Done' },
      ],
    },
    { key: 'tags',         label: 'Multi-select / Tags', type: 'multi_enum', direction: 'both', description: 'Multi-select property values' },
    { key: 'select',       label: 'Select Property',  type: 'enum',       direction: 'both',    description: 'Single select property (enter property name)' },
    { key: 'date',         label: 'Date Property',    type: 'date',       direction: 'both',    description: 'Date property (enter property name)' },
    { key: 'number',       label: 'Number Property',  type: 'number',     direction: 'both',    description: 'Number property (enter property name)' },
    { key: 'checkbox',     label: 'Checkbox Property',type: 'boolean',    direction: 'both',    description: 'Checkbox property (enter property name)' },
    { key: 'url',          label: 'URL Property',     type: 'text',       direction: 'both',    description: 'URL property (enter property name)' },
    { key: 'person',       label: 'Person Property',  type: 'text',       direction: 'both',    description: 'People property — email or name' },
    { key: 'database',     label: 'Database',         type: 'text',       direction: 'both',    description: 'Notion database name or ID' },
  ],

  /**
   * File-based CSV / Excel import-export. External field keys are typically
   * free-text column headers (externalFieldLabel). Named FieldMappingSets on
   * this connector are Import Profiles saved from the Items list Map Columns step.
   */
  csv: [
    { key: 'title',            label: 'Title column',            type: 'text',       direction: 'both', description: 'Primary name / headline' },
    { key: 'description',      label: 'Description column',      type: 'text',       direction: 'both' },
    { key: 'external_id',      label: 'External ID column',      type: 'text',       direction: 'both', description: 'Stable upstream key for upsert' },
    { key: 'id',               label: 'Item ID column',          type: 'text',       direction: 'both', description: 'Platform UUID for partial update' },
    { key: 'itemCategory',     label: 'Item Category column',    type: 'enum',       direction: 'both' },
    { key: 'altitudeLevel',    label: 'Altitude Level column',   type: 'enum',       direction: 'both' },
    { key: 'strategicBucket',  label: 'Strategic Bucket column', type: 'enum',       direction: 'both' },
    { key: 'work_type',        label: 'Work Type column',        type: 'text',       direction: 'both' },
    { key: 'launch_type',      label: 'Launch Type column',      type: 'text',       direction: 'both' },
    { key: 'investment_type',  label: 'Investment Type column',  type: 'text',       direction: 'both' },
    { key: 'tags',             label: 'Tags column',             type: 'multi_enum', direction: 'both' },
    { key: 'source',           label: 'Source column',           type: 'text',       direction: 'both' },
    { key: 'custom_column',    label: 'Custom / other column',   type: 'text',       direction: 'both', description: 'Any other CSV header — set via free-text label' },
  ],
};

// ── Core types ────────────────────────────────────────────────────────────────

/** One field-to-field translation rule inside a mapping set. */
export interface FieldMappingEntry {
  id: string;
  externalFieldKey: string;
  /** Free-text override — used for connector custom fields whose key isn't in the registry */
  externalFieldLabel?: string;
  internalFieldKey: string;
  direction: MappingDirection;
  enabled: boolean;
  /** Value-level mapping: only relevant when both sides are enum/multi_enum/number */
  valueMap: Array<{ externalValue: string; internalValue: string }>;
  /**
   * For an attribute target measured on more than one scale: the scale the value
   * lands on (e.g. Impact's "1–10 Numeric"). Absent = the attribute's default scale.
   */
  scaleId?: string;
}

/**
 * A named collection of field mapping rules for one connector.
 * Multiple sets per connector allow context-switching (e.g. "Default" vs "Sprint Planning").
 * On import or export the active set for that direction is resolved.
 *
 * Storage: persisted to connector_configs.field_mappings (JSONB) as FieldMappingConfig.
 * §2.1 compliance: Supabase is the system of record. No localStorage.
 */
export interface FieldMappingSet {
  id: string;
  connectorType: string;
  name: string;
  description: string;
  entries: FieldMappingEntry[];
  createdAt: string;
  updatedAt: string;
  /**
   * Data Citizenship v0 — CSV import/export profile metadata.
   * Present on connector type `csv` sets that are citizenship profiles.
   */
  citizenship?: import('./dataCitizenship/csvCitizenshipTypes').CsvCitizenshipProfileMeta;
}

/**
 * A single color→field rule for Miro imports.
 *
 * Two semantics depending on whether `value` is set:
 *
 *   • value absent  — the sticky note's TEXT is placed into `field` (e.g. blue stickies
 *     become the `current_state` of an outcome driver). Used by clarity-tool entity types.
 *
 *   • value present — the sticky note's COLOR sets `field` to a specific `value`, regardless
 *     of the note text (e.g. orange stickies → altitudeLevel = "3"). Used for items imports
 *     where color conveys metadata, not text routing.
 */
/**
 * A visual cue a rule matches on. Default (and the only one Miro uses) is the
 * fill color. The whiteboard importer adds more channels so a team can build a
 * richer "visual language". A rule with no `dimension` is a fill-color rule,
 * which keeps every previously-stored Miro config valid with no migration.
 *
 * Whiteboard Field Mappings start with six primary channels: shape_color,
 * shape, line_color, line_type, arrow_color, arrow_shape. Legacy Miro names
 * (`fill_color`, `line_style`) remain valid synonyms.
 */
export type SignifierDimension =
  | 'fill_color'    // legacy Miro synonym of shape_color
  | 'shape_color'   // preferred whiteboard name for fill / sticky color
  | 'border_color'
  | 'text_color'
  | 'shape'
  | 'line_style'    // legacy composite connector kind (arrow, dashed_line, …)
  | 'line_color'
  | 'line_type'     // solid | dashed
  | 'arrow_color'
  | 'arrow_shape';   // none | single | double

/** Dimensions that describe a node (item carrier), not a connector. */
export const NODE_SIGNIFIER_DIMENSIONS: ReadonlySet<SignifierDimension> = new Set([
  'fill_color', 'shape_color', 'border_color', 'text_color', 'shape',
]);

/** Dimensions that describe a connector / edge. */
export const CONNECTOR_SIGNIFIER_DIMENSIONS: ReadonlySet<SignifierDimension> = new Set([
  'line_style', 'line_color', 'line_type', 'arrow_color', 'arrow_shape',
]);

/** Color-bearing dimensions (token is a color name). */
export function isColorSignifierDimension(d: SignifierDimension): boolean {
  return d === 'fill_color' || d === 'shape_color' || d === 'border_color'
    || d === 'text_color' || d === 'line_color' || d === 'arrow_color';
}

export interface MiroColorRule {
  /**
   * The matched variant token. For a color dimension this is a color name
   * ('yellow', 'orange', …); for `shape` it's a shape name ('diamond', 'cloud');
   * for `line_style` a connector kind ('dashed_arrow', 'double_arrow');
   * for `line_type` 'solid'|'dashed'; for `arrow_shape` 'none'|'single'|'double'.
   * Named `color` for backward-compatible storage.
   */
  color: string;
  /**
   * Which visual channel this rule matches. Absent = 'fill_color' (the legacy
   * Miro behavior).
   */
  dimension?: SignifierDimension;
  /** Target entity field key (e.g. 'title', 'altitudeLevel', 'itemCategory', 'attribute:value') */
  field: string;
  /** Human-readable label shown in the UI */
  label?: string;
  /**
   * When set, the field is assigned this value (not the sticky text).
   * Typically an enum value string ("3" for altitudeLevel=3) or a number
   * ("10" for an impact/value attribute).
   */
  value?: string;
}

/** A rule's effective dimension, treating a missing one as fill color. */
export function ruleDimension(rule: MiroColorRule): SignifierDimension {
  return rule.dimension ?? 'fill_color';
}

/**
 * True when two dimensions refer to the same physical channel
 * (e.g. fill_color ↔ shape_color).
 */
export function dimensionsEquivalent(a: SignifierDimension, b: SignifierDimension): boolean {
  if (a === b) return true;
  const fillPair = (a === 'fill_color' || a === 'shape_color') && (b === 'fill_color' || b === 'shape_color');
  return fillPair;
}

/**
 * Per-entity-type set of color rules for Miro.
 */
export interface MiroEntityColorMap {
  id: string;
  /** Matches ImportEntityType (e.g. 'outcome_drivers', 'backplan_nodes', 'open_questions') */
  entityType: string;
  rules: MiroColorRule[];
}

// ── Semantic map (v3) rule types ──────────────────────────────────────────────
// The Settings field maps evolve into semantic maps: alongside field→field
// entries and visual signifier rules, v3 adds native-type routing and
// relationship-cue resolution. Same JSONB column, same editors — no second
// mapping UI. Spec: docs/9-exploration/semantic-connectors/02-DESIGN §2.3.

/** Routes a dialect's native object type to an internal target. */
export interface TypeMapRule {
  /** Member of the connector profile's nativeObjectTypes (e.g. 'sticky_note', 'card'). */
  nativeType: string;
  target: 'item' | 'annotation' | 'region' | 'ignore';
  /** When target = 'item': force this category (visual rules can still override). */
  itemCategory?: string;
}

/** The kind of dialect cue a relationship rule matches. */
export type RelationshipCueKind =
  | 'caption'      // text written on a connector/arrow (explicit)
  | 'symbol'       // a short marker glyph/word ("⊃", "blocks")
  | 'edge_kind'    // the line style ('normal' | 'dashed' | 'dotted' | …)
  | 'link_type'    // a typed link in the dialect (Jira epic-link, …)
  | 'containment'; // object inside a region/frame

/**
 * Resolves a dialect cue to a canonical relationship (canon 12 vocabulary
 * only). `defaultConfidence` + `basis` carry the uncertainty policy: explicit
 * cues write edges directly; spatial cues become Nexus proposals.
 */
export interface RelationshipCueRule {
  cue: { kind: RelationshipCueKind; value: string };
  /** RelationshipEdgeType value — typed as string to keep this module UI-light. */
  canonicalType: string;
  subtype?: string;
  directionality?: 'directed' | 'undirected';
  defaultConfidence: number;
  basis: 'explicit' | 'symbol' | 'visual_language' | 'spatial';
}

/**
 * The full JSONB payload stored in connector_configs.field_mappings.
 * version = 2 signals named-set format (v1 was a flat FieldMapping[] array).
 * version = 3 adds the semantic sections (typeMap, relationshipMap) — an
 * in-place, additive upgrade: every v2 config is a valid v3 config with the
 * semantic sections absent.
 */
export interface FieldMappingConfig {
  version: 2 | 3;
  sets: FieldMappingSet[];
  importSetId: string | null;
  exportSetId: string | null;
  /** Visual signifier rules (fill/border/text color, shape, line style), keyed by entityType. */
  miroColorMaps?: MiroEntityColorMap[];
  /** v3 — native object type routing. Absent = connector profile defaults apply. */
  typeMap?: TypeMapRule[];
  /** v3 — dialect cue → canonical relationship resolution. Absent = profile defaults. */
  relationshipMap?: RelationshipCueRule[];
}

/**
 * Resolve the first matching relationship rule for a set of cues, honoring the
 * precedence the whiteboard spine established: caption/symbol (explicit marks)
 * beat line style, which beats containment.
 */
export function resolveRelationshipCue(
  cues: Array<{ kind: RelationshipCueKind; value: string }>,
  rules: RelationshipCueRule[],
): RelationshipCueRule | null {
  const precedence: RelationshipCueKind[] = ['caption', 'symbol', 'link_type', 'edge_kind', 'containment'];
  for (const kind of precedence) {
    for (const cue of cues.filter(c => c.kind === kind)) {
      const norm = cue.value.toLowerCase().trim();
      const match = rules.find(
        r => r.cue.kind === kind && r.cue.value.toLowerCase().trim() === norm,
      );
      if (match) return match;
    }
  }
  return null;
}

// ── Connector label overrides (display names) ─────────────────────────────────
// Only needed when the label differs from capitalised key.
// Everything else resolves automatically from CONNECTOR_FIELDS.

const CONNECTOR_LABEL_OVERRIDES: Record<string, string> = {
  clickup: 'ClickUp',
  github:  'GitHub',
  whiteboard: 'Whiteboard Photo',
  csv: 'CSV Import/Export',
};

/**
 * All connectors that have a registered field schema.
 * This is the single source of truth — adding an entry to CONNECTOR_FIELDS
 * automatically surfaces the connector everywhere in the UI.
 */
export function getAllConnectors(): Array<{ type: string; label: string }> {
  return Object.keys(CONNECTOR_FIELDS).map(type => ({
    type,
    label: CONNECTOR_LABEL_OVERRIDES[type]
      ?? (type.charAt(0).toUpperCase() + type.slice(1)),
  }));
}

// ── Default mapping sets ──────────────────────────────────────────────────────

/**
 * Returns a ready-to-use FieldMappingSet for Jira with sensible defaults.
 * Covers the most common field mappings that work without workspace-specific
 * enum customisation. Teams can clone/edit as their starting point.
 *
 * Value maps are omitted for free-text fields and for enums where the
 * Priorities.ai value names match Jira's (e.g. labels → cue).
 */
/**
 * What a linked Linear issue brings over unless the workspace maps otherwise:
 * its description, and its priority into the custom field "Priority" (Linear
 * sends priority as a number; the value list names it). Linear has no custom
 * fields of its own (its API schema carries none), so this is the one place
 * the five built-in priorities get their names. Persisted into the Linear
 * connector's field mappings the first time an item is linked, so Settings →
 * Field Mappings shows and governs it from then on.
 */
export const LINEAR_PRIORITY_FIELD_KEY = 'priority';
export const LINEAR_PRIORITY_VALUE_MAP: ReadonlyArray<{ externalValue: string; internalValue: string }> = [
  { externalValue: '1', internalValue: 'Urgent' },
  { externalValue: '2', internalValue: 'High' },
  { externalValue: '3', internalValue: 'Medium' },
  { externalValue: '4', internalValue: 'Low' },
  { externalValue: '0', internalValue: 'No priority' },
];

export function getDefaultLinearMappingSet(): FieldMappingSet {
  const now = new Date().toISOString();
  return {
    id: 'linear-default',
    connectorType: 'linear',
    name: 'Linear default',
    description: 'What a linked Linear issue brings over: its description, and its priority into the custom field “Priority”.',
    createdAt: now,
    updatedAt: now,
    entries: [
      {
        id: 'linear-default-description',
        externalFieldKey: 'description',
        internalFieldKey: 'description',
        direction: 'import',
        enabled: true,
        valueMap: [],
      },
      {
        id: 'linear-default-priority',
        externalFieldKey: 'priority',
        internalFieldKey: `custom_field:${LINEAR_PRIORITY_FIELD_KEY}`,
        direction: 'import',
        enabled: true,
        valueMap: LINEAR_PRIORITY_VALUE_MAP.map((v) => ({ ...v })),
      },
    ],
  };
}

export function getDefaultJiraMappingSet(): FieldMappingSet {
  const now = new Date().toISOString();
  return {
    id: 'jira-default',
    connectorType: 'jira',
    name: 'Default',
    description: 'Recommended starting-point mapping for Jira ↔ Priorities.ai',
    createdAt: now,
    updatedAt: now,
    entries: [
      // ── Core identity ────────────────────────────────────────────────────
      {
        id: 'jira-default-summary',
        externalFieldKey: 'summary',
        internalFieldKey: 'title',
        direction: 'both',
        enabled: true,
        valueMap: [],
      },
      {
        id: 'jira-default-description',
        externalFieldKey: 'description',
        internalFieldKey: 'description',
        direction: 'both',
        enabled: true,
        valueMap: [],
      },
      // Issue key stored as external_key — used to avoid duplicate creation on re-import
      {
        id: 'jira-default-issue-key',
        externalFieldKey: 'issue_key',
        internalFieldKey: 'external_key',
        direction: 'import',
        enabled: true,
        valueMap: [],
      },

      // ── Classification ───────────────────────────────────────────────────
      {
        id: 'jira-default-issue-type',
        externalFieldKey: 'issue_type',
        internalFieldKey: 'category',
        direction: 'both',
        enabled: true,
        // Jira → Priorities.ai category value map
        valueMap: [
          { externalValue: 'Bug',         internalValue: 'bug' },
          { externalValue: 'Story',       internalValue: 'feature' },
          { externalValue: 'Epic',        internalValue: 'initiative' },
          { externalValue: 'Task',        internalValue: 'idea' },
          { externalValue: 'Improvement', internalValue: 'improvement' },
          { externalValue: 'New Feature', internalValue: 'feature' },
          { externalValue: 'Technical',   internalValue: 'infrastructure' },
          { externalValue: 'Sub-task',    internalValue: 'idea' },
        ],
      },
      {
        id: 'jira-default-priority',
        externalFieldKey: 'priority',
        internalFieldKey: 'attribute:importance',
        direction: 'both',
        enabled: true,
        // Jira 5-level priority → 1-10 importance score bands
        valueMap: [
          { externalValue: 'Highest', internalValue: '9' },
          { externalValue: 'High',    internalValue: '7' },
          { externalValue: 'Medium',  internalValue: '5' },
          { externalValue: 'Low',     internalValue: '3' },
          { externalValue: 'Lowest',  internalValue: '1' },
        ],
      },

      // ── Status ───────────────────────────────────────────────────────────
      {
        id: 'jira-default-status',
        externalFieldKey: 'status',
        internalFieldKey: 'status',
        direction: 'import',
        enabled: true,
        valueMap: [
          { externalValue: 'Backlog',     internalValue: 'draft' },
          { externalValue: 'Open',        internalValue: 'active' },
          { externalValue: 'In Progress', internalValue: 'active' },
          { externalValue: 'In Review',   internalValue: 'active' },
          { externalValue: 'Done',        internalValue: 'archived' },
          { externalValue: 'Closed',      internalValue: 'archived' },
          { externalValue: 'Blocked',     internalValue: 'active' },
        ],
      },
      {
        id: 'jira-default-resolution',
        externalFieldKey: 'resolution',
        internalFieldKey: 'resolution',
        direction: 'import',
        enabled: true,
        valueMap: [
          { externalValue: 'Fixed',            internalValue: 'completed' },
          { externalValue: 'Done',             internalValue: 'completed' },
          { externalValue: "Won't Fix",        internalValue: 'wont_do' },
          { externalValue: 'Duplicate',        internalValue: 'duplicate' },
          { externalValue: 'Incomplete',       internalValue: 'out_of_scope' },
          { externalValue: 'Cannot Reproduce', internalValue: 'out_of_scope' },
        ],
      },

      // ── Effort / sizing ──────────────────────────────────────────────────
      {
        id: 'jira-default-story-points',
        externalFieldKey: 'story_points',
        internalFieldKey: 'attribute:effort',
        direction: 'both',
        enabled: true,
        valueMap: [],
      },

      // ── Scheduling ───────────────────────────────────────────────────────
      {
        id: 'jira-default-due-date',
        externalFieldKey: 'due_date',
        internalFieldKey: 'due_date',
        direction: 'both',
        enabled: true,
        valueMap: [],
      },
      {
        id: 'jira-default-fix-version',
        externalFieldKey: 'fix_version',
        internalFieldKey: 'target_release',
        direction: 'both',
        enabled: true,
        valueMap: [],
      },

      // ── Hierarchy ────────────────────────────────────────────────────────
      {
        id: 'jira-default-epic-link',
        externalFieldKey: 'epic_link',
        internalFieldKey: 'parent_item',
        direction: 'both',
        enabled: true,
        valueMap: [],
      },
      {
        id: 'jira-default-parent',
        externalFieldKey: 'parent',
        internalFieldKey: 'parent_item',
        direction: 'both',
        enabled: false, // disabled by default — conflicts with epic_link on non-sub-task projects
        valueMap: [],
      },

      // ── Tagging / routing ────────────────────────────────────────────────
      {
        id: 'jira-default-labels',
        externalFieldKey: 'labels',
        internalFieldKey: 'cue',
        direction: 'both',
        enabled: true,
        valueMap: [],
      },
      {
        id: 'jira-default-components',
        externalFieldKey: 'components',
        internalFieldKey: 'stewardship_bucket',
        direction: 'import',
        enabled: false, // opt-in — component names rarely match bucket names verbatim
        valueMap: [],
      },

      // ── People ───────────────────────────────────────────────────────────
      {
        id: 'jira-default-assignee',
        externalFieldKey: 'assignee',
        internalFieldKey: 'attribute:confidence', // placeholder — workspace maps to shapingOwnerId
        direction: 'import',
        enabled: false, // disabled until user maps assignee → their preferred ownership field
        valueMap: [],
      },

      // ── Demand signal ────────────────────────────────────────────────────
      {
        id: 'jira-default-votes',
        externalFieldKey: 'votes',
        internalFieldKey: 'attribute:reach',
        direction: 'import',
        enabled: true,
        valueMap: [],
      },
    ],
  };
}

// ── DB service (§2.1 compliance — connector_configs.field_mappings) ───────────
//
// All field mapping configuration is stored in the `field_mappings` JSONB column
// on the `connector_configs` table (migration 399). No localStorage.

import {
  fetchConnectorConfigsFromDB,
  saveConnectorConfigToDB,
  type ConnectorType,
  type FieldMapping,
} from './connectors';

function emptyConfig(): FieldMappingConfig {
  return { version: 2, sets: [], importSetId: null, exportSetId: null };
}

/** Read a stored connector_configs.field_mappings value (v1 / v2 / v3) into a config. */
export function parseFieldMappingConfig(raw: unknown): FieldMappingConfig {
  if (!raw) return emptyConfig();
  if (Array.isArray(raw)) return emptyConfig(); // v1 flat array
  const obj = raw as Record<string, unknown>;
  // v3 is additive over v2 — both parse to the same shape (semantic sections optional).
  if (obj.version === 2 || obj.version === 3) return obj as unknown as FieldMappingConfig;
  return emptyConfig();
}

/**
 * Load the FieldMappingConfig for one connector from DB.
 * Returns an empty config if no row exists yet.
 */
export async function loadFieldMappingConfig(
  workspaceId: string,
  connectorType: string,
): Promise<FieldMappingConfig> {
  const { data } = await fetchConnectorConfigsFromDB(workspaceId);
  const row = (data ?? []).find(r => r.connectorType === connectorType);
  return parseFieldMappingConfig(row?.fieldMappings ?? null);
}

/**
 * Persist the FieldMappingConfig for one connector to connector_configs.field_mappings.
 */
export async function saveFieldMappingConfig(
  workspaceId: string,
  connectorType: string,
  config: FieldMappingConfig,
): Promise<{ error: string | null }> {
  const { error } = await saveConnectorConfigToDB(
    workspaceId,
    connectorType as ConnectorType,
    // Cast: the column is JSONB; FieldMappingConfig is stored verbatim
    { fieldMappings: config as unknown as FieldMapping[] },
  );
  return { error };
}

// ── Miro color-map helpers ────────────────────────────────────────────────────

/**
 * Load all Miro color maps for one workspace from the Miro connector config.
 */
export async function loadMiroColorMaps(workspaceId: string): Promise<MiroEntityColorMap[]> {
  const config = await loadFieldMappingConfig(workspaceId, 'miro');
  return config.miroColorMaps ?? [];
}

/**
 * Persist updated Miro color maps back to the Miro connector config in DB.
 * Merges with existing FieldMappingConfig so sets/importSetId/exportSetId are preserved.
 */
export async function saveMiroColorMaps(
  workspaceId: string,
  colorMaps: MiroEntityColorMap[],
): Promise<{ error: string | null }> {
  const existing = await loadFieldMappingConfig(workspaceId, 'miro');
  return saveFieldMappingConfig(workspaceId, 'miro', { ...existing, miroColorMaps: colorMaps });
}

/**
 * Get the color rules for one entity type.
 * Returns an empty array when no mapping is configured.
 */
export function getColorRulesForEntity(
  colorMaps: MiroEntityColorMap[],
  entityType: string,
): MiroColorRule[] {
  return colorMaps.find(m => m.entityType === entityType)?.rules ?? [];
}

// ── Semantic (v3) section helpers ─────────────────────────────────────────────

/**
 * Persist the v3 semantic sections for one connector, preserving everything
 * else in the config. Marks the config version 3.
 */
export async function saveSemanticRules(
  workspaceId: string,
  connectorType: string,
  sections: { typeMap?: TypeMapRule[]; relationshipMap?: RelationshipCueRule[] },
): Promise<{ error: string | null }> {
  const existing = await loadFieldMappingConfig(workspaceId, connectorType);
  return saveFieldMappingConfig(workspaceId, connectorType, {
    ...existing,
    version: 3,
    ...(sections.typeMap !== undefined ? { typeMap: sections.typeMap } : {}),
    ...(sections.relationshipMap !== undefined ? { relationshipMap: sections.relationshipMap } : {}),
  });
}

/**
 * Given a Miro sticky note fill color string, resolve which field it maps to.
 * Falls back to `defaultField` (usually 'title') when no rule matches.
 */
export function resolveColorField(
  color: string | undefined,
  rules: MiroColorRule[],
  defaultField = 'title',
): string {
  if (!color || rules.length === 0) return defaultField;
  const normalized = color.toLowerCase().trim();
  const match = rules.find(r => r.color.toLowerCase().trim() === normalized);
  return match?.field ?? defaultField;
}
